/**
 * Exibe uma notificação do sistema para mensagens que chegam com a aba em
 * foco — caso em que o SDK entrega o payload para a página e não desenha nada.
 *
 * Usa o service worker quando ele está disponível (`showNotification` é o
 * caminho suportado em todos os navegadores e o único que funciona em mobile)
 * e cai para o construtor `Notification` quando não há SW no controle, que
 * ainda cobre desktop.
 */

import type { PushMessage } from './types';

export type ForegroundPresenter = (message: PushMessage) => void | Promise<void>;

export const presentSystemNotification: ForegroundPresenter = async (message) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }

  const title = message.title ?? 'Meiuca';
  const options: NotificationOptions = {
    body: message.body,
    // Sem tag, duas notificações seguidas empilham. Com a tag do tipo, a nova
    // substitui a anterior do mesmo tipo — o operador vê o estado atual, não
    // uma pilha.
    tag: message.data.type,
    data: { ...message.data, link: message.link },
  };

  const registration = await activeServiceWorker();
  if (registration) {
    await registration.showNotification(title, options);
    return;
  }

  try {
    const notification = new Notification(title, options);
    if (message.link) {
      notification.onclick = () => {
        window.focus();
        window.location.assign(message.link as string);
        notification.close();
      };
    }
  } catch {
    // `new Notification()` é ilegal em alguns contextos (Chrome Android).
    // Sem SW e sem construtor não há como exibir — silenciar é o resto.
  }
};

async function activeServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return undefined;
  }
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return undefined;
  }
}
